import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from "../../App"; 
import DashboardHeader from "../../components/app_components/DashboardHeader";
import MobileBottomNav from "../../components/app_components/mobile/MobileBottomNav";
import { Home, BellDot, Bell, ShoppingBag, Settings, Users, ShoppingCart, CreditCard, RefreshCw, Zap, Shield, Share2 } from 'lucide-react';
import { useTranslation } from '../../contexts/LanguageContext';

const MobileConnectBank = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, validateToken, httpClient } = useAuth(); // Get auth context
  
  const [unreadCount, setUnreadCount] = useState(0);
  // Add premium status state
  const [isPremium, setIsPremium] = useState(false);

  const fetchUnreadNotifications = async () => {
    try {
      if (!httpClient) return;

      const response = await httpClient.request('/api/notifications');
      
      if (!response.ok) {
        throw new Error('Failed to fetch notifications');
      }

      const notifications = await response.json();
      const unread = notifications.filter(notif => !notif.read_status).length;
      setUnreadCount(unread);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    }
  };

  // Fetch premium status function
  const fetchPremiumStatus = async () => {
    try {
      if (!user) {
        setIsPremium(false);
        return;
      }

      // Try dedicated premium status endpoint
      try {
        const premiumRes = await httpClient.request('/api/user/premium-status');
        if (premiumRes.ok) {
          const premiumData = await premiumRes.json();
          setIsPremium(premiumData.isPremium);
          return;
        }
      } catch (premiumError) {
        console.error('Error fetching premium status:', premiumError);
      }

      // Fallback to user account endpoint
      const fallbackRes = await httpClient.request('/api/user/account');
      if (fallbackRes.ok) {
        const fallbackData = await fallbackRes.json();
        setIsPremium(fallbackData.userPlan === 'premium');
      }
    } catch (error) {
      console.error('Error fetching user premium status:', error);
      setIsPremium(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchUnreadNotifications();
      fetchPremiumStatus(); // Fetch premium status
      const interval = setInterval(() => {
        fetchUnreadNotifications();
      }, 30000);
      return () => clearInterval(interval);
    } else {
      navigate("/demo");
    }
  }, [user, navigate]);

  const navItems = [
    { icon: Home, label: t('connectBank.nav.home'), path: "/mobile-dashboard" },
    { 
      icon: unreadCount > 0 ? BellDot : Bell,
      label: t('connectBank.nav.notifications'),
      path: "/mobile-notifications",
      badge: unreadCount > 0 ? unreadCount : null
    },
    { icon: ShoppingCart, label: t('connectBank.nav.marketplace'), path: "/mobile-marketplace" },
    { icon: Share2, label: t('connectBank.nav.shareSubs'), path: "/mobile-share-subs" },
    { icon: ShoppingBag, label: t('connectBank.nav.connectBank'), path: "/mobile-connectBank" },
    { icon: Users, label: t('connectBank.nav.partners'), path: "/mobile-partners" },
    { icon: Settings, label: t('connectBank.nav.settings'), path: "/mobile-account-settings" },
  ];

  const features = [
    {
      icon: CreditCard,
      title: t('connectBank.features.automaticTracking.title'),
      description: t('connectBank.features.automaticTracking.description')
    },
    {
      icon: RefreshCw,
      title: t('connectBank.features.realTimeUpdates.title'),
      description: t('connectBank.features.realTimeUpdates.description')
    },
    {
      icon: Zap,
      title: t('connectBank.features.quickActions.title'),
      description: t('connectBank.features.quickActions.description')
    },
    {
      icon: Shield,
      title: t('connectBank.features.secureConnection.title'),
      description: t('connectBank.features.secureConnection.description')
    }
  ];

  // Show loading if user is not authenticated
  if (!user) {
    return (
      <div className="flex flex-col h-screen bg-gray-50 justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <DashboardHeader isAuthenticated={!!user} user={user} onLogout={logout} />

      <main className="flex-1 overflow-auto pb-20">
        <div className="p-4 space-y-4">
          {/* Header Section - Updated with conditional premium gradient */}
          <div className={`${isPremium 
            ? 'bg-gradient-to-r from-[#253985] to-amber-500' 
            : 'bg-[#253985]'} text-white p-4 rounded-lg transition-all duration-500`}>
            <h1 className="text-xl font-bold">{t('connectBank.title')}</h1>
            <p className="text-white/90 text-sm mt-1">{t('connectBank.subtitle')}</p>
          </div>

          {/* Main Content */}
          <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100 space-y-6">
            <div className="text-center space-y-4">
              <CreditCard className="h-12 w-12 mx-auto text-[#253985]" />
              <h2 className="text-xl font-bold text-gray-800">{t('connectBank.comingSoon.title')}</h2>
              <p className="text-gray-600 text-sm">
                {t('connectBank.comingSoon.mobileDescription')}
              </p>
            </div>

            {/* Features Grid */}
            <div className="grid grid-cols-1 gap-4">
              {features.map((feature, index) => (
                <div key={index} className="p-4 border border-gray-200 rounded-lg">
                  <feature.icon className="h-6 w-6 text-[#253985] mb-2" />
                  <h3 className="font-semibold text-gray-800">{feature.title}</h3>
                  <p className="text-gray-600 text-sm">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      <MobileBottomNav 
        navItems={navItems} 
        currentPath={location.pathname}
        maxVisibleItems={5}
        navigate={navigate}
        className="fixed bottom-0 w-full"
      />
    </div>
  );
};

export default MobileConnectBank;